import type { AxiosError, AxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';

export type ApiError = {
  code?: string;
  message?: string;
  statusCode?: number;
};

export type InviteLinkMeta =
  | {
      exists: true;
      expiresAt: string | null;
      createdAt: string;
    }
  | null;

export type CreateInviteLinkRequest = {
  expiresInDays?: number;
};

export type CreateInviteLinkResult = {
  url: string;
  expiresAt: string | null;
};

export type JoinWorkspaceRequest = {
  token: string;
};

export type JoinWorkspaceResult = {
  workspaceId: string;
};

function toApiError(err: unknown): ApiError {
  const e = err as AxiosError<any>;
  const statusCode = e?.response?.status;
  const data = e?.response?.data;

  if (data && (data.code || data.message)) {
    return {
      code: data.code,
      message: data.message,
      statusCode: data.statusCode ?? statusCode,
    };
  }

  return {
    code: 'UNKNOWN',
    message: 'Request failed',
    statusCode,
  };
}

/**
 * WS-RECOVERY-404-1 (TR-2026-10-20-14c): invite calls are about the invite's (or the
 * join token's) workspace, not the active one, so a 403/404 here must not clear the
 * user's active workspace selection.
 */
const NOT_ACTIVE_WORKSPACE: AxiosRequestConfig = { skipStaleWorkspaceRecovery: true };

export const workspaceInvitesApi = {
  async getActiveInviteLink(workspaceId: string): Promise<InviteLinkMeta> {
    try {
      // Your api.ts interceptor unwraps { data: T } into T.
      // Controller returns formatResponse(payload), so we receive payload directly.
      const res = await api.get(`/workspaces/${workspaceId}/invite-link`, NOT_ACTIVE_WORKSPACE);
      return (res as unknown as InviteLinkMeta) ?? null;
    } catch (err) {
      throw toApiError(err);
    }
  },

  async createInviteLink(
    workspaceId: string,
    body: CreateInviteLinkRequest = {},
  ): Promise<CreateInviteLinkResult> {
    try {
      const res = await api.post(`/workspaces/${workspaceId}/invite-link`, body, NOT_ACTIVE_WORKSPACE);
      const data = res as unknown as CreateInviteLinkResult;
      if (!data?.url) {
        throw { code: 'INVALID_RESPONSE', message: 'Missing invite url', statusCode: 500 } satisfies ApiError;
      }
      return data;
    } catch (err) {
      throw toApiError(err);
    }
  },

  async joinWorkspace(body: JoinWorkspaceRequest): Promise<JoinWorkspaceResult> {
    try {
      const res = await api.post('/workspaces/join', body, NOT_ACTIVE_WORKSPACE);
      const data = res as unknown as JoinWorkspaceResult;
      if (!data?.workspaceId) {
        throw { code: 'INVALID_RESPONSE', message: 'Missing workspaceId', statusCode: 500 } satisfies ApiError;
      }
      return data;
    } catch (err) {
      throw toApiError(err);
    }
  },

  async revokeActiveInviteLink(workspaceId: string): Promise<void> {
    try {
      await api.delete(`/workspaces/${workspaceId}/invite-link/active`, NOT_ACTIVE_WORKSPACE);
    } catch (err) {
      throw toApiError(err);
    }
  },
};
